// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  EyeIcon,
  TruckIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: () => string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// City coordinates for map display
const cityCoordinates: { [key: string]: [number, number] } = {
  'Chicago, IL': [41.8781, -87.6298],
  'Detroit, MI': [42.3314, -83.0458],
  'Los Angeles, CA': [34.0522, -118.2437],
  'Phoenix, AZ': [33.4484, -112.0740],
  'Atlanta, GA': [33.7490, -84.3880],
  'Miami, FL': [25.7617, -80.1918],
  'Dallas, TX': [32.7767, -96.7970],
  'Houston, TX': [29.7604, -95.3698],
  'Seattle, WA': [47.6062, -122.3321],
  'Denver, CO': [39.7392, -104.9903],
  'Newark, NJ': [40.7357, -74.1724],
  'Boston, MA': [42.3601, -71.0589],
  'Minneapolis, MN': [44.9778, -93.2650],
  'Milwaukee, WI': [43.0389, -87.9065],
  'Savannah, GA': [32.0809, -81.0912],
  'Charlotte, NC': [35.2271, -80.8431],
  'San Jose, CA': [37.3382, -121.8863],
  'Sacramento, CA': [38.5816, -121.4944],
  'Kansas City, MO': [39.0997, -94.5786],
  'St. Louis, MO': [38.6270, -90.1994],
};

// Tracking event interface
interface TrackingEvent {
  timestamp: string;
  location: string;
  status: string;
  description: string;
  coordinates?: [number, number];
}

interface Shipment {
  id: string;
  reference: string;
  status: 'planned' | 'in_transit' | 'delivered' | 'delayed' | 'cancelled';
  origin: string;
  originCity: string;
  destination: string;
  destinationCity: string;
  supplier: string;
  carrier: string;
  pallets: number;
  weight: number;
  scheduledPickup: string;
  scheduledDelivery: string;
  actualDelivery?: string;
  eta?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  mode: 'FTL' | 'LTL' | 'Intermodal' | 'Air' | 'Ocean';
  temperature?: string;
  trackingUpdates: number;
}

const mockShipments: Shipment[] = [
  {
    id: 'SHP-001',
    reference: 'PO-2024-78542',
    status: 'in_transit',
    origin: 'DC-Chicago',
    originCity: 'Chicago, IL',
    destination: 'WH-Detroit',
    destinationCity: 'Detroit, MI',
    supplier: 'Midwest Manufacturing Co.',
    carrier: 'Swift Logistics',
    pallets: 24,
    weight: 18500,
    scheduledPickup: '2024-01-10 06:00',
    scheduledDelivery: '2024-01-10 14:00',
    eta: '2024-01-10 13:45',
    priority: 'high',
    mode: 'FTL',
    trackingUpdates: 12,
  },
  {
    id: 'SHP-002',
    reference: 'PO-2024-78543',
    status: 'delayed',
    origin: 'Port-LA',
    originCity: 'Los Angeles, CA',
    destination: 'DC-Phoenix',
    destinationCity: 'Phoenix, AZ',
    supplier: 'Pacific Imports Ltd.',
    carrier: 'Desert Express',
    pallets: 48,
    weight: 42000,
    scheduledPickup: '2024-01-09 08:00',
    scheduledDelivery: '2024-01-10 10:00',
    eta: '2024-01-10 15:30',
    priority: 'critical',
    mode: 'FTL',
    temperature: '35-45°F',
    trackingUpdates: 8,
  },
  {
    id: 'SHP-003',
    reference: 'PO-2024-78544',
    status: 'delivered',
    origin: 'DC-Atlanta',
    originCity: 'Atlanta, GA',
    destination: 'Store-Miami-12',
    destinationCity: 'Miami, FL',
    supplier: 'Southern Foods Inc.',
    carrier: 'Coastal Freight',
    pallets: 16,
    weight: 12800,
    scheduledPickup: '2024-01-09 04:00',
    scheduledDelivery: '2024-01-09 18:00',
    actualDelivery: '2024-01-09 17:32',
    priority: 'medium',
    mode: 'FTL',
    trackingUpdates: 15,
  },
  {
    id: 'SHP-004',
    reference: 'PO-2024-78545',
    status: 'planned',
    origin: 'WH-Dallas',
    originCity: 'Dallas, TX',
    destination: 'DC-Houston',
    destinationCity: 'Houston, TX',
    supplier: 'Texas Parts Supply',
    carrier: 'Lone Star Transport',
    pallets: 32,
    weight: 28000,
    scheduledPickup: '2024-01-11 05:00',
    scheduledDelivery: '2024-01-11 12:00',
    priority: 'medium',
    mode: 'FTL',
    trackingUpdates: 0,
  },
  {
    id: 'SHP-005',
    reference: 'PO-2024-78546',
    status: 'in_transit',
    origin: 'Port-Seattle',
    originCity: 'Seattle, WA',
    destination: 'DC-Denver',
    destinationCity: 'Denver, CO',
    supplier: 'Northwest Electronics',
    carrier: 'Mountain Freight Lines',
    pallets: 20,
    weight: 15000,
    scheduledPickup: '2024-01-10 02:00',
    scheduledDelivery: '2024-01-11 18:00',
    eta: '2024-01-11 16:00',
    priority: 'low',
    mode: 'Intermodal',
    trackingUpdates: 6,
  },
  {
    id: 'SHP-006',
    reference: 'PO-2024-78547',
    status: 'in_transit',
    origin: 'DC-Newark',
    originCity: 'Newark, NJ',
    destination: 'WH-Boston',
    destinationCity: 'Boston, MA',
    supplier: 'East Coast Distributors',
    carrier: 'Northeast Logistics',
    pallets: 12,
    weight: 9600,
    scheduledPickup: '2024-01-10 07:00',
    scheduledDelivery: '2024-01-10 15:00',
    eta: '2024-01-10 14:30',
    priority: 'high',
    mode: 'LTL',
    temperature: '32-38°F',
    trackingUpdates: 9,
  },
  {
    id: 'SHP-007',
    reference: 'PO-2024-78548',
    status: 'delivered',
    origin: 'DC-Minneapolis',
    originCity: 'Minneapolis, MN',
    destination: 'Store-Milwaukee-5',
    destinationCity: 'Milwaukee, WI',
    supplier: 'Heartland Products',
    carrier: 'Great Lakes Transport',
    pallets: 8,
    weight: 6400,
    scheduledPickup: '2024-01-09 06:00',
    scheduledDelivery: '2024-01-09 14:00',
    actualDelivery: '2024-01-09 13:45',
    priority: 'low',
    mode: 'LTL',
    trackingUpdates: 11,
  },
  {
    id: 'SHP-008',
    reference: 'PO-2024-78549',
    status: 'delayed',
    origin: 'Port-Savannah',
    originCity: 'Savannah, GA',
    destination: 'DC-Charlotte',
    destinationCity: 'Charlotte, NC',
    supplier: 'Atlantic Cargo Services',
    carrier: 'Carolina Freight',
    pallets: 52,
    weight: 48000,
    scheduledPickup: '2024-01-09 10:00',
    scheduledDelivery: '2024-01-10 08:00',
    eta: '2024-01-10 14:00',
    priority: 'critical',
    mode: 'FTL',
    trackingUpdates: 5,
  },
  {
    id: 'SHP-009',
    reference: 'PO-2024-78550',
    status: 'planned',
    origin: 'WH-San-Jose',
    originCity: 'San Jose, CA',
    destination: 'DC-Sacramento',
    destinationCity: 'Sacramento, CA',
    supplier: 'Silicon Valley Tech',
    carrier: 'California Express',
    pallets: 6,
    weight: 2400,
    scheduledPickup: '2024-01-12 08:00',
    scheduledDelivery: '2024-01-12 14:00',
    priority: 'high',
    mode: 'LTL',
    trackingUpdates: 0,
  },
  {
    id: 'SHP-010',
    reference: 'PO-2024-78551',
    status: 'in_transit',
    origin: 'DC-Kansas-City',
    originCity: 'Kansas City, MO',
    destination: 'WH-St-Louis',
    destinationCity: 'St. Louis, MO',
    supplier: 'Central Midwest Supply',
    carrier: 'Gateway Logistics',
    pallets: 28,
    weight: 22400,
    scheduledPickup: '2024-01-10 05:00',
    scheduledDelivery: '2024-01-10 11:00',
    eta: '2024-01-10 10:45',
    priority: 'medium',
    mode: 'FTL',
    trackingUpdates: 7,
  },
];

// Generate mock tracking events for a shipment
const generateTrackingEvents = (shipment: Shipment): TrackingEvent[] => {
  const originCoords = cityCoordinates[shipment.originCity];
  const destCoords = cityCoordinates[shipment.destinationCity];

  const events: TrackingEvent[] = [];
  const pickupDate = new Date(shipment.scheduledPickup.replace(' ', 'T'));

  // Pickup event
  events.push({
    timestamp: shipment.scheduledPickup,
    location: shipment.originCity,
    status: 'picked_up',
    description: `Shipment picked up from ${shipment.origin}`,
    coordinates: originCoords,
  });

  if (shipment.status === 'planned') {
    return events.slice(0, 0); // No events yet for planned shipments
  }

  // In transit events
  if (shipment.status === 'in_transit' || shipment.status === 'delivered' || shipment.status === 'delayed') {
    events.push({
      timestamp: new Date(pickupDate.getTime() + 30 * 60000).toISOString().replace('T', ' ').slice(0, 16),
      location: shipment.originCity,
      status: 'departed',
      description: `Departed ${shipment.origin} facility`,
      coordinates: originCoords,
    });

    // Generate intermediate checkpoints
    if (originCoords && destCoords) {
      const midLat = (originCoords[0] + destCoords[0]) / 2;
      const midLng = (originCoords[1] + destCoords[1]) / 2;

      events.push({
        timestamp: new Date(pickupDate.getTime() + 4 * 3600000).toISOString().replace('T', ' ').slice(0, 16),
        location: 'In Transit',
        status: 'in_transit',
        description: 'Shipment in transit - GPS update received',
        coordinates: [midLat, midLng],
      });
    }

    if (shipment.status === 'delayed') {
      events.push({
        timestamp: new Date(pickupDate.getTime() + 6 * 3600000).toISOString().replace('T', ' ').slice(0, 16),
        location: 'In Transit',
        status: 'delayed',
        description: 'Delay reported - Traffic congestion on route',
        coordinates: destCoords ? [destCoords[0] + 0.5, destCoords[1] + 0.5] : undefined,
      });
    }
  }

  // Delivered event
  if (shipment.status === 'delivered' && shipment.actualDelivery) {
    events.push({
      timestamp: shipment.actualDelivery,
      location: shipment.destinationCity,
      status: 'arrived',
      description: `Arrived at ${shipment.destination}`,
      coordinates: destCoords,
    });

    events.push({
      timestamp: shipment.actualDelivery,
      location: shipment.destinationCity,
      status: 'delivered',
      description: `Delivery completed - Signed by receiver`,
      coordinates: destCoords,
    });
  }

  return events;
};

// Get unique values for filter dropdowns
const getUniqueCarriers = () => [...new Set(mockShipments.map(s => s.carrier))];
const getUniqueSuppliers = () => [...new Set(mockShipments.map(s => s.supplier))];

const Shipments: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const cameFromAlerts = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [modeFilter, setModeFilter] = useState<string>('');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // More Filters state
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [carrierFilter, setCarrierFilter] = useState<string>('');
  const [supplierFilter, setSupplierFilter] = useState<string>('');
  const [temperatureFilter, setTemperatureFilter] = useState<boolean>(false);
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');

  // Tracking modal state
  const [showTracking, setShowTracking] = useState(false);
  const [trackingShipment, setTrackingShipment] = useState<Shipment | null>(null);

  // Handle URL query params for deep linking from Alerts
  useEffect(() => {
    const shipmentId = searchParams.get('shipment');
    if (shipmentId) {
      const shipment = mockShipments.find(s => s.id === shipmentId);
      if (shipment) {
        cameFromAlerts.current = true;
        setTrackingShipment(shipment);
        setShowTracking(true);
        // Clear the query param after handling
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams]);

  // Handle closing tracking modal - return to Alerts if came from there
  const handleCloseTracking = () => {
    setShowTracking(false);
    setTrackingShipment(null);
    if (cameFromAlerts.current) {
      cameFromAlerts.current = false;
      navigate('/alerts');
    }
  };

  const filteredShipments = mockShipments.filter((shipment) => {
    const matchesSearch =
      shipment.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.carrier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || shipment.status === statusFilter;
    const matchesPriority = !priorityFilter || shipment.priority === priorityFilter;
    const matchesMode = !modeFilter || shipment.mode === modeFilter;
    const matchesCarrier = !carrierFilter || shipment.carrier === carrierFilter;
    const matchesSupplier = !supplierFilter || shipment.supplier === supplierFilter;
    const matchesTemperature = !temperatureFilter || !!shipment.temperature;

    // Date range filtering
    let matchesDateRange = true;
    if (dateRangeStart || dateRangeEnd) {
      const shipmentDate = new Date(shipment.scheduledDelivery.split(' ')[0]);
      if (dateRangeStart) {
        matchesDateRange = matchesDateRange && shipmentDate >= new Date(dateRangeStart);
      }
      if (dateRangeEnd) {
        matchesDateRange = matchesDateRange && shipmentDate <= new Date(dateRangeEnd);
      }
    }

    return matchesSearch && matchesStatus && matchesPriority && matchesMode && matchesCarrier && matchesSupplier && matchesTemperature && matchesDateRange;
  });

  const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);
  const paginatedShipments = filteredShipments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: Shipment['status']) => {
    const styles = {
      planned: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      in_transit: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      delayed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-500',
    };
    const labels = {
      planned: 'Planned',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      delayed: 'Delayed',
      cancelled: 'Cancelled',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPriorityBadge = (priority: Shipment['priority']) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[priority]}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const statusCounts = {
    all: mockShipments.length,
    planned: mockShipments.filter((s) => s.status === 'planned').length,
    in_transit: mockShipments.filter((s) => s.status === 'in_transit').length,
    delivered: mockShipments.filter((s) => s.status === 'delivered').length,
    delayed: mockShipments.filter((s) => s.status === 'delayed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Shipments</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Track and manage all shipments across your network
          </p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
            <PlusIcon className="w-4 h-4 mr-2" />
            New Shipment
          </button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'All Shipments', count: statusCounts.all, color: 'teal', filter: '' },
          { label: 'Planned', count: statusCounts.planned, color: 'gray', filter: 'planned' },
          { label: 'In Transit', count: statusCounts.in_transit, color: 'blue', filter: 'in_transit' },
          { label: 'Delivered', count: statusCounts.delivered, color: 'green', filter: 'delivered' },
          { label: 'Delayed', count: statusCounts.delayed, color: 'red', filter: 'delayed' },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => setStatusFilter(item.filter)}
            className={`p-4 rounded-xl border-2 transition-all ${
              statusFilter === item.filter
                ? `border-${item.color}-500 bg-${item.color}-50 dark:bg-${item.color}-900/20`
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.count}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{item.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by reference, ID, origin, destination, or carrier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Modes</option>
              <option value="FTL">FTL</option>
              <option value="LTL">LTL</option>
              <option value="Intermodal">Intermodal</option>
              <option value="Air">Air</option>
              <option value="Ocean">Ocean</option>
            </select>
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
                showMoreFilters
                  ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              More Filters
              {(carrierFilter || supplierFilter || temperatureFilter || dateRangeStart || dateRangeEnd) && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-teal-600 rounded-full">
                  {[carrierFilter, supplierFilter, temperatureFilter, dateRangeStart, dateRangeEnd].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* More Filters Panel */}
        {showMoreFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Carrier
                </label>
                <select
                  value={carrierFilter}
                  onChange={(e) => setCarrierFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Carriers</option>
                  {getUniqueCarriers().map((carrier) => (
                    <option key={carrier} value={carrier}>{carrier}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Suppliers</option>
                  {getUniqueSuppliers().map((supplier) => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delivery Date Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => setDateRangeStart(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                  <input
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => setDateRangeEnd(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Special Requirements
                </label>
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600">
                  <input
                    type="checkbox"
                    checked={temperatureFilter}
                    onChange={(e) => setTemperatureFilter(e.target.checked)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Temperature Controlled</span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setCarrierFilter('');
                  setSupplierFilter('');
                  setTemperatureFilter(false);
                  setDateRangeStart('');
                  setDateRangeEnd('');
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Shipments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Shipment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Route
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Carrier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Load
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedShipments.map((shipment) => (
                <tr
                  key={shipment.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
                  onClick={() => setSelectedShipment(shipment)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                        <TruckIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {shipment.id}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {shipment.reference}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(shipment.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm">
                      <div>
                        <div className="flex items-center text-gray-900 dark:text-white">
                          <MapPinIcon className="w-4 h-4 mr-1 text-green-500" />
                          {shipment.origin}
                        </div>
                        <div className="flex items-center text-gray-500 dark:text-gray-400 mt-1">
                          <MapPinIcon className="w-4 h-4 mr-1 text-red-500" />
                          {shipment.destination}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{shipment.carrier}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{shipment.mode}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{shipment.pallets} pallets</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {shipment.weight.toLocaleString()} lbs
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <CalendarIcon className="w-4 h-4 mr-1 text-gray-400" />
                      {shipment.scheduledDelivery.split(' ')[0]}
                    </div>
                    {shipment.eta && shipment.status === 'in_transit' && (
                      <div className="flex items-center text-sm text-blue-600 dark:text-blue-400 mt-1">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        ETA: {shipment.eta.split(' ')[1]}
                      </div>
                    )}
                    {shipment.status === 'delayed' && (
                      <div className="flex items-center text-sm text-red-600 dark:text-red-400 mt-1">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        Late: {shipment.eta?.split(' ')[1]}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPriorityBadge(shipment.priority)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedShipment(shipment);
                      }}
                      className="text-teal-600 hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredShipments.length)}
                </span>{' '}
                of <span className="font-medium">{filteredShipments.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === i + 1
                        ? 'z-10 bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-600 dark:text-teal-400'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Shipment Detail Modal */}
      {selectedShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedShipment.id}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">{selectedShipment.reference}</p>
                </div>
                <button
                  onClick={() => setSelectedShipment(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                {getStatusBadge(selectedShipment.status)}
                {getPriorityBadge(selectedShipment.priority)}
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-600 dark:text-gray-300">
                  {selectedShipment.mode}
                </span>
                {selectedShipment.temperature && (
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded text-sm text-blue-600 dark:text-blue-400">
                    {selectedShipment.temperature}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Origin</h3>
                  <p className="text-gray-900 dark:text-white font-medium">{selectedShipment.origin}</p>
                  <p className="text-gray-500 dark:text-gray-400">{selectedShipment.originCity}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Destination</h3>
                  <p className="text-gray-900 dark:text-white font-medium">{selectedShipment.destination}</p>
                  <p className="text-gray-500 dark:text-gray-400">{selectedShipment.destinationCity}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Supplier</h3>
                  <p className="text-gray-900 dark:text-white">{selectedShipment.supplier}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Carrier</h3>
                  <p className="text-gray-900 dark:text-white">{selectedShipment.carrier}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Scheduled Pickup</h3>
                  <p className="text-gray-900 dark:text-white">{selectedShipment.scheduledPickup}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Scheduled Delivery</h3>
                  <p className="text-gray-900 dark:text-white">{selectedShipment.scheduledDelivery}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Load</h3>
                  <p className="text-gray-900 dark:text-white">
                    {selectedShipment.pallets} pallets • {selectedShipment.weight.toLocaleString()} lbs
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Tracking Updates</h3>
                  <p className="text-gray-900 dark:text-white">{selectedShipment.trackingUpdates} updates</p>
                </div>
              </div>

              {selectedShipment.eta && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                    Estimated Time of Arrival
                  </h3>
                  <p className="text-blue-900 dark:text-blue-200 font-semibold text-lg">
                    {selectedShipment.eta}
                  </p>
                </div>
              )}

              {selectedShipment.actualDelivery && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">
                    Actual Delivery
                  </h3>
                  <p className="text-green-900 dark:text-green-200 font-semibold text-lg">
                    {selectedShipment.actualDelivery}
                  </p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setSelectedShipment(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setTrackingShipment(selectedShipment);
                  setShowTracking(true);
                  setSelectedShipment(null);
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Track Shipment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {showTracking && trackingShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Track Shipment: {trackingShipment.id}
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  {trackingShipment.originCity} → {trackingShipment.destinationCity}
                </p>
              </div>
              <button
                onClick={handleCloseTracking}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Map */}
                <div className="bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  {(() => {
                    const originCoords = cityCoordinates[trackingShipment.originCity];
                    const destCoords = cityCoordinates[trackingShipment.destinationCity];
                    const trackingEvents = generateTrackingEvents(trackingShipment);
                    const routeCoords = trackingEvents
                      .filter((e) => e.coordinates)
                      .map((e) => e.coordinates as [number, number]);

                    if (originCoords && destCoords) {
                      const centerLat = (originCoords[0] + destCoords[0]) / 2;
                      const centerLng = (originCoords[1] + destCoords[1]) / 2;

                      return (
                        <MapContainer
                          center={[centerLat, centerLng]}
                          zoom={5}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker position={originCoords}>
                            <Popup>
                              <strong>Origin:</strong> {trackingShipment.origin}
                              <br />
                              {trackingShipment.originCity}
                            </Popup>
                          </Marker>
                          <Marker position={destCoords}>
                            <Popup>
                              <strong>Destination:</strong> {trackingShipment.destination}
                              <br />
                              {trackingShipment.destinationCity}
                            </Popup>
                          </Marker>
                          {routeCoords.length >= 2 && (
                            <Polyline
                              positions={routeCoords}
                              color={trackingShipment.status === 'delayed' ? '#ef4444' : '#0d9488'}
                              weight={3}
                              dashArray={trackingShipment.status === 'in_transit' ? '10, 10' : undefined}
                            />
                          )}
                        </MapContainer>
                      );
                    }
                    return (
                      <div className="h-full flex items-center justify-center text-gray-500">
                        Map coordinates not available
                      </div>
                    );
                  })()}
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Tracking Timeline
                  </h3>
                  <div className="space-y-0">
                    {(() => {
                      const events = generateTrackingEvents(trackingShipment);
                      if (events.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            <ClockIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No tracking updates yet</p>
                            <p className="text-sm">Shipment is planned but not yet in transit</p>
                          </div>
                        );
                      }

                      return events.map((event, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                event.status === 'delivered'
                                  ? 'bg-green-100 dark:bg-green-900/30'
                                  : event.status === 'delayed'
                                  ? 'bg-red-100 dark:bg-red-900/30'
                                  : event.status === 'in_transit'
                                  ? 'bg-blue-100 dark:bg-blue-900/30'
                                  : 'bg-gray-100 dark:bg-gray-700'
                              }`}
                            >
                              {event.status === 'delivered' ? (
                                <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                              ) : event.status === 'delayed' ? (
                                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                              ) : event.status === 'in_transit' ? (
                                <TruckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <MapPinIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              )}
                            </div>
                            {index < events.length - 1 && (
                              <div className="w-0.5 h-12 bg-gray-200 dark:bg-gray-700" />
                            )}
                          </div>
                          <div className="pb-6">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {event.description}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {event.location}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {event.timestamp}
                            </p>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Shipment Summary */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                    <div className="mt-1">{getStatusBadge(trackingShipment.status)}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Carrier</p>
                    <p className="mt-1 font-medium text-gray-900 dark:text-white">{trackingShipment.carrier}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Mode</p>
                    <p className="mt-1 font-medium text-gray-900 dark:text-white">{trackingShipment.mode}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {trackingShipment.status === 'delivered' ? 'Delivered' : 'ETA'}
                    </p>
                    <p className="mt-1 font-medium text-gray-900 dark:text-white">
                      {trackingShipment.actualDelivery || trackingShipment.eta || trackingShipment.scheduledDelivery}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={handleCloseTracking}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipments;
